import React, { useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  Edit,
  ToggleLeft,
  ToggleRight,
  Eye,
  Car,
  MapPin,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  Calendar,
  User,
  Info,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import {
  usePricing,
  useTogglePricingStatus,
  useDeletePricing,
} from "@/api-services/hooks/usePricing";
import { useVehicleCategories } from "@/api-services/hooks/usePricing";
import {
  BasePricing,
  VehicleCategory,
} from "@/api-services/hooks/pricingTypes";
import DeletePricingConfirmation from "@/components/pricing/DeletePricingConfirmation";

const ViewPricingPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pricingId } = useParams<{ pricingId: string }>();
  const pricingIdNum = pricingId ? parseInt(pricingId, 10) : 0;

  // State
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [pricingToDelete, setPricingToDelete] = useState<{
    id: number;
    details: string;
  } | null>(null);

  // Fetch pricing data
  const {
    data: pricingData,
    loading,
    error,
  } = usePricing(pricingIdNum, {
    onError: (err) => {
      toast.error(`Failed to load pricing: ${err.message}`);
    },
  });

  // Fetch vehicle categories for display
  const { data: vehicleCategories } = useVehicleCategories({
    enabled: true,
  });

  // Mutations
  const { mutate: toggleStatus, isLoading: isToggling } =
    useTogglePricingStatus({
      onSuccess: async (data, pricingId) => {
        toast.success("Pricing status updated successfully!");
        // Invalidate all pricings queries to ensure fresh data
        await queryClient.invalidateQueries({ queryKey: ['pricings'] });
        // Invalidate the specific pricing query to refresh the view page
        await queryClient.invalidateQueries({ queryKey: ['pricing', pricingIdNum] });
      },
      onError: (error) => {
        toast.error(`Failed to update pricing status: ${error.message}`);
      },
    });

  const { mutate: deletePricing, isLoading: isDeleting } = useDeletePricing({
    onSuccess: (data, pricingId) => {
      toast.success("Pricing deleted successfully!");
      navigate("/admin/base-pricing");
    },
    onError: (error, pricingId) => {
      toast.error(`Failed to delete pricing: ${error.message}`);
    },
  });

  // Helper functions - wrapped in useCallback to prevent infinite re-renders
  // Removed vehicleCategories dependency to prevent infinite loops
  const getVehicleCategoryName = useCallback(
    (vehicleCategoryId: number): string => {
      if (!vehicleCategories) return `Vehicle ID: ${vehicleCategoryId}`;
      const category = vehicleCategories.find(
        (vc) => vc.vehicleCategoryId === vehicleCategoryId
      );
      return (
        category?.vehicleCategoryName || `Vehicle ID: ${vehicleCategoryId}`
      );
    },
    []
  ); // Empty dependency array prevents recreation

  const getVehicleCategoryDetails = useCallback(
    (vehicleCategoryId: number): VehicleCategory | undefined => {
      if (!vehicleCategories) return undefined;
      return vehicleCategories.find(
        (vc) => vc.vehicleCategoryId === vehicleCategoryId
      );
    },
    []
  ); // Empty dependency array prevents recreation

  const getStatusBadge = useCallback((isActive: boolean) => {
    return isActive
      ? {
          variant: "default" as const,
          className: "bg-success text-success-foreground",
          label: "Active",
        }
      : {
          variant: "secondary" as const,
          className: "bg-muted text-muted-foreground",
          label: "Inactive",
        };
  }, []);

  const formatCurrency = useCallback((amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  }, []);

  const formatDate = useCallback((dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  const handleToggleStatus = () => {
    toggleStatus(pricingIdNum);
  };

  const handleDeleteClick = () => {
    if (pricingData) {
      const details = `${getVehicleCategoryName(
        pricingData.vehicleCategoryId
      )} - ${pricingData.city?.cityName || "Unknown City"}`;
      setPricingToDelete({ id: pricingIdNum, details });
      setDeleteConfirmationOpen(true);
    }
  };

  const handleDeleteConfirm = (pricingId: number) => {
    deletePricing(pricingId);
    setDeleteConfirmationOpen(false);
    setPricingToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmationOpen(false);
    setPricingToDelete(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading pricing details...</p>
        </div>
      </div>
    );
  }

  if (error || !pricingData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <XCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Pricing Not Found</h3>
            <p className="text-muted-foreground mb-4">
              {error?.message ||
                "The requested pricing rule could not be found."}
            </p>
            <Button onClick={() => navigate("/admin/base-pricing")}>
              Back to Pricing
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const vehicleCategory = getVehicleCategoryDetails(
    pricingData.vehicleCategoryId
  );
  const statusBadge = getStatusBadge(pricingData.isActive);

  return (
    <div className="space-y-6 animate-fade-in max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/admin/base-pricing")}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Pricing
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Pricing Details
            </h1>
            <p className="text-muted-foreground">
              View detailed information about this pricing rule
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/admin/base-pricing/edit/${pricingIdNum}`)}
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit Pricing
          </Button>
          <Button
            variant="outline"
            onClick={handleToggleStatus}
            disabled={isToggling}
            className={
              pricingData.isActive ? "text-orange-600" : "text-green-600"
            }
          >
            {pricingData.isActive ? (
              <>
                <ToggleLeft className="w-4 h-4 mr-2" />
                Deactivate
              </>
            ) : (
              <>
                <ToggleRight className="w-4 h-4 mr-2" />
                Activate
              </>
            )}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeleteClick}
            disabled={isDeleting}
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Delete Pricing
          </Button>
        </div>
      </div>

      {/* Status Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <DollarSign className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground">
                  {getVehicleCategoryName(pricingData.vehicleCategoryId)}
                </h2>
                <p className="text-muted-foreground">
                  {pricingData.city?.cityName || "Unknown City"}
                </p>
              </div>
            </div>
            <Badge className={statusBadge.className}>{statusBadge.label}</Badge>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="w-5 h-5" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">
                  Vehicle Category
                </p>
                <p className="font-medium">
                  {getVehicleCategoryName(pricingData.vehicleCategoryId)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">City</p>
                <p className="font-medium">
                  {pricingData.city?.cityName || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pricing ID</p>
                <p className="font-medium">#{pricingData.pricingId}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <Badge className={statusBadge.className}>
                  {statusBadge.label}
                </Badge>
              </div>
            </div>

            {vehicleCategory && (
              <div className="mt-4 p-3 bg-muted/30 rounded-lg">
                <h4 className="font-semibold mb-2 text-sm">Vehicle Details</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">
                      Max Passengers:
                    </span>
                    <span className="ml-2 font-medium">
                      {vehicleCategory.maxPassengerCount}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">
                      Luggage Volume:
                    </span>
                    <span className="ml-2 font-medium">
                      {vehicleCategory.maxLuggageVolume} m³
                    </span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pricing Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Pricing Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Base Fare</span>
                <span className="font-bold text-green-600 text-lg">
                  {formatCurrency(pricingData.baseFare)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Rate per KM</span>
                <span className="font-semibold text-blue-600">
                  {formatCurrency(pricingData.ratePerKm)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Rate per Minute</span>
                <span className="font-semibold text-orange-600">
                  {formatCurrency(pricingData.ratePerMinute)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Minimum Fare</span>
                <span className="font-semibold text-purple-600">
                  {formatCurrency(pricingData.minimumFare)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Night Charges</span>
                <span className="font-semibold text-indigo-600">
                  {formatCurrency(pricingData.nightCharges)}
                </span>
              </div>
              <Separator />
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Toll Fee</span>
                <Badge
                  variant={
                    pricingData.tollFeeApplicable ? "default" : "secondary"
                  }
                >
                  {pricingData.tollFeeApplicable
                    ? "Applicable"
                    : "Not Applicable"}
                </Badge>
              </div>
            </div>

            {/* Pricing Calculator Preview */}
            <div className="mt-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold mb-3 text-sm text-blue-800">
                <TrendingUp className="inline w-4 h-4 mr-1" />
                Sample Fare Calculation
              </h4>
              <div className="space-y-2 text-sm text-blue-900">
                <div className="flex justify-between">
                  <span>Base Fare (5km, 15min):</span>
                  <span className="font-medium">
                    {formatCurrency(
                      Math.max(
                        pricingData.minimumFare,
                        pricingData.baseFare +
                          pricingData.ratePerKm * 5 +
                          pricingData.ratePerMinute * 15 +
                          pricingData.nightCharges
                      )
                    )}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Metadata */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Metadata
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Created By</p>
                <p className="font-medium">
                  {pricingData.createdBy || "System"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Created Date</p>
                <p className="font-medium">
                  {formatDate(pricingData.createdDate || "")}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Last Modified By
                </p>
                <p className="font-medium">
                  {pricingData.modifiedBy || pricingData.createdBy || "System"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Last Modified Date
                </p>
                <p className="font-medium">
                  {formatDate(
                    pricingData.modifiedDate || pricingData.createdDate || ""
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Additional Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Usage Guidelines */}
            <div className="space-y-3">
              <h4 className="font-semibold">Usage Guidelines</h4>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
                  <span>Base fare is charged at the start of every ride</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
                  <span>
                    Distance charges apply based on actual route distance
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
                  <span>
                    Time charges apply during wait time and traffic delays
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
                  <span>Night charges apply during specified hours</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
                  <span>Minimum fare guarantees driver earnings</span>
                </div>
              </div>
            </div>

            {/* Pricing Impact */}
            <div className="space-y-3">
              <h4 className="font-semibold">Pricing Impact</h4>
              <div className="space-y-2 text-sm">
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 text-blue-800 mb-2">
                    <TrendingUp className="w-4 h-4" />
                    <span className="font-medium">Competitive Analysis</span>
                  </div>
                  <p className="text-blue-700">
                    This pricing is positioned to be competitive while
                    maintaining profitability for drivers.
                  </p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2 text-amber-800 mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="font-medium">Important Notes</span>
                  </div>
                  <p className="text-amber-700">
                    This pricing rule applies to all rides within the specified
                    city and vehicle category.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal */}
      <DeletePricingConfirmation
        open={deleteConfirmationOpen}
        onOpenChange={handleDeleteCancel}
        pricingDetails={pricingToDelete?.details || ""}
        pricingId={pricingToDelete?.id || 0}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default ViewPricingPage;
